import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { settleTrailerAfterOrderClose } from "@/lib/trailer-settle";
import { toast } from "sonner";
import { useTenant } from "@/hooks/useTenant";
import { useUserRole } from "@/hooks/useUserRole";
import { useEapiAuth } from "@/hooks/useEapiAuth";
import { removeOrderBolsOnCancel } from "@/lib/bol-cancellation-cleanup";
import { cancelOtherActiveLegTrips } from "@/lib/eapi-leg-cancel";
import { isAlreadyCancelledUpstreamError } from "@/lib/eapi-cancel-utils";
import { notifyOrderStatus } from "@/lib/notify-order-status";
import type { EapiClient } from "@/lib/eapi-client";
import { recordChangeEvents } from "@/hooks/useChangeEventPrompt";
import type { ChangeEventReason } from "@/lib/change-events";

export interface CancelOptions {
  reason?: string | null;
  notes?: string | null;
  /**
   * Canonical reason enum captured by `ChangeReasonDialog`. When provided,
   * a `public.change_events` row is recorded after the cancel succeeds.
   */
  changeEventReason?: ChangeEventReason | null;
  /** Required when `changeEventReason === 'other'`. */
  changeEventOther?: string | null;
}

interface CancelInput {
  orderId: string;
  options?: CancelOptions;
}

interface BulkCancelInput {
  orderIds: string[];
  options?: CancelOptions;
}

interface OrderRowForCancel {
  id: string;
  unique_id: string | null;
  tenant_id: string | null;
  eapi_trip_id: string | null;
  status: string | null;
  trailer_id?: string | null;
}

/**
 * Cancel a single order with full Sync-then-Persist semantics:
 *   1. Fetch authoritative row state.
 *   2. Reject if already delivered — unless the caller is an admin, who may
 *      cancel delivered orders as a data correction. The DB function
 *      `cancel_order_locally` enforces the same rule server-side.
 *   3. If `eapi_trip_id` is set, cancel the upstream trip first.
 *   4. Fan out per-leg trip cancellations (multi-leg orders).
 *   5. Opt into the DB cancel guard, then write `status='canceled'` locally.
 *   6. Best-effort BoL S3 + DB cleanup.
 *
 * Delivered-order cancels skip every EAPI side effect: the upstream trips have
 * already completed, so cancelling them would fail and abort the whole
 * operation. They also skip BoL cleanup — a delivered order's documents are
 * billing artifacts and must not be deleted by a retroactive cancel.
 *
 * Throws on EAPI failure so the caller can surface "X failed" in the bulk path
 * and the local row stays intact (no orphaned upstream trip).
 */
async function cancelOneOrder(args: {
  orderId: string;
  options: CancelOptions | undefined;
  eapiClient: EapiClient | null;
  eapiReady: boolean;
  activeTenantSlug: string | null | undefined;
  initiator?: string;
  isAdmin?: boolean;
}): Promise<OrderRowForCancel> {
  const { orderId, options, eapiClient, eapiReady, activeTenantSlug, isAdmin } = args;
  const initiator = args.initiator ?? "dispatcher-ui";

  const { data: row, error: rowErr } = await supabase
    .from("orders")
    .select("id, unique_id, tenant_id, eapi_trip_id, status, trailer_id")
    .eq("id", orderId)
    .maybeSingle<OrderRowForCancel>();

  if (rowErr) throw rowErr;
  if (!row) throw new Error(`Order ${orderId} not found`);
  const wasDelivered = row.status === "delivered";
  if (wasDelivered && !isAdmin) {
    throw new Error(
      `Order ${row.unique_id ?? orderId} is already delivered. Only an admin can cancel a delivered order.`,
    );
  }
  if (row.status === "canceled") {
    // No-op but still report so callers can refresh BoL/cleanup state if needed.
    return row;
  }


  // ── Step 1: Sync upstream cancel BEFORE touching the local DB ──
  // Skipped entirely for an admin cancel of a delivered order: those trips
  // have already completed upstream, so there is nothing to cancel and the
  // attempt would only fail and block the local correction.
  if (row.eapi_trip_id && !wasDelivered) {
    if (!eapiReady || !eapiClient) {
      throw new Error(
        "External trip system not ready — cannot cancel order with an active EAPI trip. Please retry shortly.",
      );
    }
    try {
      await eapiClient.cancelTrip(row.eapi_trip_id, options?.reason ?? undefined);
    } catch (err: any) {
      if (isAlreadyCancelledUpstreamError(err)) {
        // Upstream trip was already cancelled (most likely by a prior
        // partial-success attempt where the local DB write failed). Treat as
        // success so retries can finish clearing local state instead of
        // looping forever on the same upstream 404/409.
        console.log(
          `[cancel] upstream trip ${row.eapi_trip_id} already cancelled, proceeding with local cancel`,
        );
      } else {
        throw new Error(
          `Failed to cancel upstream EAPI trip ${row.eapi_trip_id}: ${err?.message ?? err}. Local order was NOT canceled.`,
        );
      }
    }
  }

  // ── Step 2: Per-leg cancel fan-out (multi-leg orders) ──
  // Multi-leg orders have NO `eapi_trip_id` on the parent row, so step 1
  // doesn't apply. The upstream EAPI trips live on `public.trips` keyed by
  // each leg, and we MUST cancel them before marking the local order
  // canceled — otherwise we leave orphan upstream trips while clearing our
  // local pointers to them.
  if (!wasDelivered) {
    if (!eapiReady || !eapiClient) {
      throw new Error(
        "External trip system not ready — cannot cancel order with active per-leg EAPI trips. Please retry shortly.",
      );
    }
    await cancelOtherActiveLegTrips(
      orderId,
      row.eapi_trip_id ?? null,
      eapiClient,
      options?.reason || "Order cancelled",
      "order_cancelled",
    );
  }


  // ── Step 3: Atomic local cancel (opt-in + UPDATE in one transaction) ──
  // Using cancel_order_locally avoids a PostgREST connection-pool race where
  // the GUC set by allow_canceled_with_eapi_trip_for_request() could land on
  // a different backend connection than the subsequent UPDATE.
  const { error: updErr } = await supabase.rpc("cancel_order_locally", {
    _order_id: orderId,
    _reason: options?.reason ?? null,
    _notes: options?.notes ?? null,
  });

  if (updErr) {
    // Upstream trip(s) were already cancelled by step 1/2. Surface a
    // distinct error so the user knows local state is the problem, not
    // EAPI — otherwise the next retry will loop on a 4xx upstream response.
    throw new Error(
      `Upstream trip cancelled but local order update failed: ${
        updErr.message ?? String(updErr)
      }. Please retry or escalate.`,
    );
  }

  // ── Step 3a: Best-effort order_change_log row ──
  // cancel_order_locally writes only audit_logs (admin-only under RLS), so
  // without this the order history and CancellationBanner can't say who
  // cancelled.
  if (row.tenant_id) {
    const { data: userData } = await supabase.auth.getUser();
    const { error: logErr } = await supabase.from("order_change_log").insert({
      order_id: orderId,
      field_name: "status",
      old_value: row.status ?? null,
      new_value: "canceled",
      changed_by: userData?.user?.email ?? "Unknown",
      tenant_id: row.tenant_id,
    });
    if (logErr) console.warn("[cancel] order_change_log insert failed", logErr);
  }

  // ── Step 3b: Release the trailer ──
  // A canceled order never arrives anywhere. Without this the trailer stays
  // `in_transit` indefinitely and drops out of Yard Audit / checkout lists.
  await settleTrailerAfterOrderClose({
    orderId,
    trailerHumanId: row.trailer_id ?? null,
    tenantId: row.tenant_id ?? null,
    outcome: "canceled",
    eapiClient,
  });

  // ── Step 4: Best-effort BoL cleanup (S3 metadata trigger handles its side) ──
  // Never for a delivered order: its BoLs are billing artifacts for freight
  // that actually moved, and a retroactive admin cancel must not delete them.
  if (!wasDelivered && row.unique_id && row.tenant_id && activeTenantSlug) {
    void removeOrderBolsOnCancel({
      orderId,
      orderUniqueId: row.unique_id,
      tenantId: row.tenant_id,
      tenantSlug: activeTenantSlug,
    });
  }


  // ── Step 4b: Best-effort change_events row (Titus reason-code tracking) ──
  // Captured separately from the legacy `cancellation_reason` text so the
  // dispatcher's reason becomes queryable by downstream BI dashboards.
  // Awaited so that insert failures (RLS, validation, network) are visible
  // to the dispatcher as a non-fatal toast instead of being silently dropped
  // — the cancel itself is already committed and is not rolled back.
  if (options?.changeEventReason && row.tenant_id) {
    try {
      const result = await recordChangeEvents([
        {
          orderId,
          tenantId: row.tenant_id,
          eapiTripId: row.eapi_trip_id ?? null,
          eventType: "trip_cancelled",
          reason: options.changeEventReason,
          otherText: options.changeEventOther ?? null,
          previousValue: { status: row.status },
          newValue: { status: "canceled" },
        },
      ]);
      if (result.error) {
        console.warn("[cancel] recordChangeEvents failed", result.error);
        toast.warning(
          `Order ${row.unique_id ?? orderId} canceled, but reason code was not recorded: ${result.error}. Please re-enter via order history.`,
          { duration: 8000 },
        );
      }
    } catch (err: any) {
      console.warn("[cancel] recordChangeEvents threw", err);
      toast.warning(
        `Order ${row.unique_id ?? orderId} canceled, but reason code was not recorded: ${err?.message ?? err}. Please re-enter via order history.`,
        { duration: 8000 },
      );
    }
  }

  // ── Step 5: Best-effort customer email notification ──
  // Eligibility (non-AURY, has email, root-only, de-dup) is enforced inside
  // send-email itself. This is fire-and-forget — failures never affect the
  // cancel result.
  void notifyOrderStatus({
    orderId,
    newStatus: "canceled",
    initiator,
    tenantSlug: activeTenantSlug,
    // Pre-cancel status, captured in step 1. send-email uses it to skip
    // customer mail for orders cancelled while still `booked`.
    previousStatus: row.status ?? null,
  });

  return row;
}

/**
 * SOX Compliance: Order deletion is blocked at the database level.
 * Orders can only be canceled (soft-delete via status change).
 *
 * Sync-then-Persist: this hook is the canonical client-side entry point for
 * cancelling orders. It guarantees the upstream EAPI trip is cancelled BEFORE
 * the local DB row is updated, so we never leave orphaned trips behind.
 * Per-leg trips on multi-leg orders are also fanned out and cancelled.
 */
export function useOrderCancellation() {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenant();
  const { eapiClient, isReady: eapiReady } = useEapiAuth();
  // Admins may cancel delivered orders (data correction); dispatchers may not.
  // `cancel_order_locally` enforces the same rule server-side, so this only
  // decides which side effects run and which error message the user sees.
  const { isAdmin } = useUserRole();

  const ensureTenantContext = async () => {
    if (activeTenant) {
      await supabase.rpc("set_tenant_slug", { _slug: activeTenant });
    }
  };

  const invalidateAfterCancel = (orderId: string) => {
    queryClient.invalidateQueries({ queryKey: ["orders"] });
    queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
    queryClient.invalidateQueries({ queryKey: ["order-detail", orderId] });
    queryClient.invalidateQueries({ queryKey: ["bol-count", orderId] });
    queryClient.invalidateQueries({ queryKey: ["bol-images", orderId] });
    queryClient.invalidateQueries({ queryKey: ["bol-images-count", orderId] });
    queryClient.invalidateQueries({ queryKey: ["bol-extractions", orderId] });
  };

  const cancelMutation = useMutation({
    mutationFn: async (input: string | CancelInput) => {
      const { orderId, options } =
        typeof input === "string" ? { orderId: input, options: undefined } : input;
      await ensureTenantContext();
      return cancelOneOrder({
        orderId,
        options,
        eapiClient,
        eapiReady,
        activeTenantSlug: activeTenant,
        initiator: "dispatcher-ui",
        isAdmin,
      });
    },
    onSuccess: (result) => {
      invalidateAfterCancel(result.id);
      toast.success(`Order ${result.unique_id ?? ""} canceled`);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to cancel order");
    },
  });

  const bulkCancelMutation = useMutation({
    mutationFn: async (input: string[] | BulkCancelInput) => {
      const { orderIds, options } = Array.isArray(input)
        ? { orderIds: input, options: undefined }
        : input;
      await ensureTenantContext();

      const succeeded: OrderRowForCancel[] = [];
      const failed: { orderId: string; uniqueId: string | null; message: string }[] = [];

      // Sequential to keep EAPI rate-limit safe and avoid partial-state races.
      for (const orderId of orderIds) {
        try {
          const row = await cancelOneOrder({
            orderId,
            options,
            eapiClient,
            eapiReady,
            activeTenantSlug: activeTenant,
            initiator: "bulk-cancel",
            isAdmin,
          });
          succeeded.push(row);
        } catch (err: any) {
          // Try to look up unique_id for friendlier reporting.
          const { data: meta } = await supabase
            .from("orders")
            .select("unique_id")
            .eq("id", orderId)
            .maybeSingle();
          failed.push({
            orderId,
            uniqueId: meta?.unique_id ?? null,
            message: err?.message ?? String(err),
          });
        }
      }

      return { succeeded, failed };
    },
    onSuccess: (result) => {
      for (const row of result.succeeded) {
        invalidateAfterCancel(row.id);
      }
      if (result.succeeded.length > 0) {
        toast.success(`Canceled ${result.succeeded.length} order(s)`);
      }
      if (result.failed.length > 0) {
        const ids = result.failed.map((f) => f.uniqueId ?? f.orderId).join(", ");
        toast.error(
          `Failed to cancel ${result.failed.length} order(s): ${ids}. ` +
            `First error: ${result.failed[0].message}`,
          { duration: 8000 },
        );
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to cancel orders");
    },
  });

  return {
    cancelOrder: (orderId: string, options?: CancelOptions) =>
      cancelMutation.mutate({ orderId, options }),
    cancelOrderAsync: (orderId: string, options?: CancelOptions) =>
      cancelMutation.mutateAsync({ orderId, options }),
    bulkCancel: (orderIds: string[], options?: CancelOptions) =>
      bulkCancelMutation.mutate({ orderIds, options }),
    bulkCancelAsync: (orderIds: string[], options?: CancelOptions) =>
      bulkCancelMutation.mutateAsync({ orderIds, options }),
    isCanceling: cancelMutation.isPending || bulkCancelMutation.isPending,
  };
}

// Legacy export — kept for backward compatibility but will throw at DB level
export function useOrderDeletion() {
  const { cancelOrder, isCanceling } = useOrderCancellation();
  return {
    deleteOrder: cancelOrder,
    isDeleting: isCanceling,
  };
}
